import React, { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Input, InputNumber, DatePicker, TimePicker, Select, Checkbox } from 'antd';
import moment from 'moment-timezone';
import Cards from 'design-components/Cards';
import {
  createSchedule,
  modifySchedule,
  fetchSchedule,
  fetchScheduleTimeline,
} from 'state/actions/schedule';
import { fetchScreensForSite } from 'state/actions/screens';
import { fetchMedia } from 'state/actions/media';
import MediaSelectModal from './MediaSelectModal';
import TimelineSequence from './TimelineSequence';
import 'components/MediaForm/MediaForm.css';
import './ScheduleForm.css';
import './MediaSelectModal.css';

const { TextArea } = Input;

const SCHEDULE_TYPES = [
  { key: 'specificTime', label: 'Specific Time' },
  { key: 'specificDays', label: 'Specific Days' },
  { key: 'allDay', label: 'All Day' },
];

const DAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tues' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

const defaultForm = {
  name: '',
  description: '',
  scheduleType: 'specificTime',
  startDate: null,
  endDate: null,
  startTime: null,
  endTime: null,
  daysOfWeek: [],
  targetImpression: '',
  mediaIds: [],
  screenIds: [],
};

const rowsOf = (b) =>
  Array.isArray(b?.rows) && b.rows.length ? b.rows : [b?.row ?? 0];

const ConflictBanner = ({ conflict, onClose }) => {
  if (!conflict) return null;
  const lines = [];
  if (conflict.selfOverlap) {
    const { a, b, rows } = conflict.selfOverlap;
    lines.push(
      `Own blocks overlap on lane(s) ${(rows || []).join(',') || '?'}: ${a?.startOffset}s and ${b?.startOffset}s.`
    );
  }
  if (Array.isArray(conflict.capacityViolations)) {
    conflict.capacityViolations.forEach((c) =>
      lines.push(
        `${c.screenName || 'Screen'} lane(s) ${(c.rows || []).join(',') || '?'}: needs ${c.requiredEnd}s, loop is ${c.loopDuration}s.`
      )
    );
  }
  if (Array.isArray(conflict.conflicts)) {
    conflict.conflicts.forEach((c) => {
      const againstName = c.against?.name || c.against?.sourceId || 'block';
      const againstSrc = c.against?.source || '';
      lines.push(
        `Overlaps ${againstSrc} "${againstName}" on lane(s) ${(c.rows || []).join(',') || '?'} at ${c.startOffset}s (${c.duration}s).`
      );
    });
  }
  if (lines.length === 0 && conflict.message) lines.push(conflict.message);
  return (
    <div className="schedule-conflict-banner">
      <div className="schedule-conflict-title">
        {conflict.message || 'Schedule conflict'}
        <button type="button" className="schedule-conflict-close" onClick={onClose}>
          ×
        </button>
      </div>
      <ul>
        {lines.map((l, i) => (
          <li key={i}>{l}</li>
        ))}
      </ul>
    </div>
  );
};

const ScheduleForm = ({ history }) => {
  const { siteId, siteName, id } = useParams();
  const dispatch = useDispatch();

  const { list, screens, mediaList, loading } = useSelector(
    (state) => ({
      list: state.schedule.list,
      screens: state.screens.list,
      mediaList: state.media.list,
      loading: state.schedule.loading,
    }),
    shallowEqual
  );

  const [form, setForm] = useState(defaultForm);
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const [selectedMediaCache, setSelectedMediaCache] = useState({});
  const [timeline, setTimeline] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [conflict, setConflict] = useState(null);
  const hydratedFromTimelineRef = React.useRef(false);

  useEffect(() => {
    if (siteId) {
      dispatch(fetchScreensForSite({ siteId }));
      dispatch(fetchMedia({ siteId }));
      if (!list || list.length === 0) {
        dispatch(fetchSchedule({ siteId }));
      }
    }
  }, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) return;
    const existing = (list || []).find((s) => s._id === id);
    if (!existing) return;
    if (Array.isArray(existing.blocks) && existing.blocks.length > 0) {
      hydratedFromTimelineRef.current = true;
    }
    setForm({
      name: existing.name || '',
      description: existing.description || '',
      scheduleType: existing.scheduleType || 'specificTime',
      startDate: existing.startDate || null,
      endDate: existing.endDate || null,
      startTime: existing.startTime || null,
      endTime: existing.endTime || null,
      daysOfWeek: existing.daysOfWeek || [],
      targetImpression: existing.targetImpression || '',
      mediaIds: (existing.mediaIds || []).map((m) =>
        typeof m === 'string' ? m : m?._id || m?.id
      ),
      screenIds: (existing.screenIds || []).map((s) =>
        typeof s === 'string' ? s : s?._id || s?.id
      ),
    });
    console.log('[ScheduleForm] hydrate from existing', {
      hasBlocks: Array.isArray(existing.blocks),
      blocksLen: existing.blocks?.length,
      existing,
    });
    if (Array.isArray(existing.blocks) && existing.blocks.length > 0) {
      const scheduleMediaIds = (existing.mediaIds || []).map((m) =>
        typeof m === 'string' ? m : m?._id || m?.id
      );
      const mapped = existing.blocks
        .map((b) => {
          const sid =
            typeof b.screenId === 'string'
              ? b.screenId
              : b.screenId?._id || b.screenId?.id;
          if (!sid) return null;
          const bMediaIds = Array.isArray(b.mediaIds) && b.mediaIds.length
            ? b.mediaIds.map((m) =>
                typeof m === 'string' ? m : m?._id || m?.id
              )
            : scheduleMediaIds;
          return {
            screenId: sid,
            rows: rowsOf(b),
            startOffset: b.startOffset,
            duration: b.duration,
            mediaIds: bMediaIds,
            blockId: b._id,
          };
        })
        .filter(Boolean);
      if (mapped.length > 0) setBlocks(mapped);
    }
    const cache = {};
    (existing.mediaIds || []).forEach((m) => {
      if (m && typeof m === 'object' && m._id) cache[m._id] = m;
    });
    if (Object.keys(cache).length > 0) {
      setSelectedMediaCache((prev) => ({ ...prev, ...cache }));
    }
  }, [id, list]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const totalDuration = useMemo(() => {
    return (form.mediaIds || []).reduce((sum, mid) => {
      const m =
        selectedMediaCache[mid] ||
        (mediaList || []).find((x) => x._id === mid);
      return sum + (m?.duration || 0);
    }, 0);
  }, [form.mediaIds, selectedMediaCache, mediaList]);

  const refetchTimeline = () => {
    if (!siteId || !form.screenIds || form.screenIds.length === 0) {
      setTimeline([]);
      return Promise.resolve();
    }
    return fetchScheduleTimeline({ siteId, screenIds: form.screenIds })
      .then((data) => setTimeline(data || []))
      .catch(() => setTimeline([]));
  };

  useEffect(() => {
    if (!siteId || !form.screenIds || form.screenIds.length === 0) {
      setTimeline([]);
      return;
    }
    let cancelled = false;
    fetchScheduleTimeline({ siteId, screenIds: form.screenIds })
      .then((data) => {
        if (!cancelled) setTimeline(data);
      })
      .catch(() => {
        if (!cancelled) setTimeline([]);
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, form.screenIds]);

  useEffect(() => {
    if (!id) return;
    if (hydratedFromTimelineRef.current) return;
    if (!timeline || timeline.length === 0) return;
    if (blocks.length > 0) {
      hydratedFromTimelineRef.current = true;
      return;
    }
    const own = [];
    const seen = new Set();
    timeline.forEach((tl) => {
      (tl.rows || []).forEach((laneRow) => {
        (laneRow.blocks || []).forEach((b) => {
          if (b.source !== 'schedule') return;
          if (b.sourceId !== id) return;
          const key = `${tl.screenId}:${b.sourceId}:${rowsOf(b).join(',')}:${b.startOffset}`;
          if (seen.has(key)) return;
          seen.add(key);
          own.push({
            screenId: tl.screenId,
            rows: rowsOf(b),
            startOffset: b.startOffset,
            duration: b.duration,
            mediaIds: Array.isArray(b.mediaIds) && b.mediaIds.length
              ? b.mediaIds.map((m) =>
                  typeof m === 'string' ? m : m?._id || m?.id
                )
              : (form.mediaIds || []),
          });
        });
      });
    });
    console.log('[ScheduleForm] hydrate from timeline', {
      id,
      ownCount: own.length,
      own,
      currentBlocksLen: blocks.length,
    });
    if (own.length > 0) {
      setBlocks(own);
      hydratedFromTimelineRef.current = true;
    }
  }, [id, timeline]);

  useEffect(() => {
    if (!form.screenIds || form.screenIds.length === 0) return;
    const keep = new Set(form.screenIds);
    setBlocks((prev) => {
      const filtered = prev.filter((b) => keep.has(b.screenId));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [form.screenIds]);

  const toggleDay = (dayKey) => {
    setForm((f) => {
      const set = new Set(f.daysOfWeek);
      if (set.has(dayKey)) set.delete(dayKey);
      else set.add(dayKey);
      return { ...f, daysOfWeek: Array.from(set) };
    });
  };

  const goBack = () => history.push(`/${siteId}/information/${siteName}`);

  const isValid = () => {
    if (!form.name || !form.description) return false;
    if (!form.startDate || !form.endDate) return false;
    if (!form.screenIds || form.screenIds.length === 0) return false;
    if (form.scheduleType === 'specificTime' && (!form.startTime || !form.endTime))
      return false;
    if (form.scheduleType === 'specificDays' && form.daysOfWeek.length === 0)
      return false;
    return true;
  };

  const handleSubmit = async () => {
    if (!isValid()) return;
    setConflict(null);

    const finalBlocks = blocks.map((b) => {
      const out = {
        screenId: b.screenId,
        rows: rowsOf(b),
        startOffset: b.startOffset,
        mediaIds: b.mediaIds && b.mediaIds.length ? b.mediaIds : form.mediaIds,
        duration: b.duration,
      };
      if (b.blockId) out.blockId = b.blockId;
      return out;
    });
    console.log('[ScheduleForm] submit blocks', {
      stateBlocks: blocks,
      finalBlocks,
    });

    const unionMediaIds = Array.from(
      new Set([
        ...(form.mediaIds || []),
        ...finalBlocks.flatMap((b) => b.mediaIds || []),
      ])
    );

    const payload = {
      name: form.name,
      description: form.description,
      siteId,
      scheduleType: form.scheduleType,
      startDate: form.startDate,
      endDate: form.endDate,
      startTime: form.scheduleType === 'specificTime' ? form.startTime : undefined,
      endTime: form.scheduleType === 'specificTime' ? form.endTime : undefined,
      daysOfWeek: form.scheduleType === 'specificDays' ? form.daysOfWeek : undefined,
      targetImpression: form.targetImpression
        ? Number(form.targetImpression)
        : undefined,
      mediaIds: unionMediaIds,
      blocks: finalBlocks,
    };

    const onDone = async () => {
      await refetchTimeline();
      goBack();
    };

    if (!id) {
      dispatch(createSchedule(payload, onDone, (body) => setConflict(body)));
      return;
    }

    dispatch(
      modifySchedule({ id, ...payload }, onDone, (body) => setConflict(body))
    );
  };

  const dateValue = (v) => (v ? moment(v) : null);
  const timeValue = (v) => (v ? moment(v, 'HH:mm') : null);

  return (
    <div className="media-form schedule-form">
      <MediaSelectModal
        visible={mediaModalOpen}
        onCancel={() => setMediaModalOpen(false)}
        onSelect={(ids, items) => {
          update('mediaIds', ids);
          const cache = { ...selectedMediaCache };
          items.forEach((m) => {
            cache[m._id] = m;
          });
          setSelectedMediaCache(cache);
          setMediaModalOpen(false);
        }}
        siteId={siteId}
        initialSelected={form.mediaIds}
      />
      <div className="schedule-form-header">
        <h2>{id ? 'Edit Schedule' : 'Add New Schedule'}</h2>
        <div className="schedule-form-actions">
          <button type="button" className="btn-back" onClick={goBack}>
            <i className="feather-arrow-left" /> Back
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!isValid() || loading}
            onClick={handleSubmit}
          >
            Submit
          </button>
        </div>
      </div>

      <form className="form-wrapper" onSubmit={(e) => e.preventDefault()}>
        <div>
          <h3 className="section-title">Schedule Details</h3>

          <div className="form">
            <label className="form-label">
              Name <span className="is-required">*</span>
            </label>
            <Input
              placeholder="Enter Schedule's Name"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
            />
          </div>

          <div className="form">
            <label className="form-label">
              Description <span className="is-required">*</span>
            </label>
            <TextArea
              rows={5}
              placeholder="Description"
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
            />
          </div>

          <div className="form">
            <label className="form-label">
              Target Impression <span className="is-required">*</span>
            </label>
            <InputNumber
              size="large"
              placeholder="Enter target impression numbers"
              value={form.targetImpression}
              onChange={(v) => update('targetImpression', v)}
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <div>
          <h3 className="section-title">General Scheduling</h3>

          <div className="form">
            <label className="form-label">Schedule Type</label>
            <div className="schedule-type-buttons">
              {SCHEDULE_TYPES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`schedule-btn ${
                    form.scheduleType === t.key ? 'active' : ''
                  }`}
                  onClick={() => update('scheduleType', t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {form.scheduleType === 'specificDays' && (
            <div className="form">
              <label className="form-label">
                Days of Week <span className="is-required">*</span>
              </label>
              <div className="days-row">
                {DAYS.map((d) => (
                  <Checkbox
                    key={d.key}
                    checked={form.daysOfWeek.includes(d.key)}
                    onChange={() => toggleDay(d.key)}
                  >
                    {d.label}
                  </Checkbox>
                ))}
              </div>
            </div>
          )}

          <div className="date-row">
            <div className="date-col">
              <div className="form">
                <label className="form-label">
                  Start Date <span className="is-required">*</span>
                </label>
                <DatePicker
                  format="DD/MM/YYYY"
                  placeholder="DD/MM/YYYY"
                  value={dateValue(form.startDate)}
                  onChange={(d) =>
                    update('startDate', d ? d.toISOString() : null)
                  }
                  style={{ width: '100%' }}
                />
              </div>
            </div>
            <div className="date-col">
              <div className="form">
                <label className="form-label">
                  End Date <span className="is-required">*</span>
                </label>
                <DatePicker
                  format="DD/MM/YYYY"
                  placeholder="DD/MM/YYYY"
                  value={dateValue(form.endDate)}
                  onChange={(d) =>
                    update('endDate', d ? d.toISOString() : null)
                  }
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </div>

          {form.scheduleType === 'specificTime' && (
            <div className="date-row">
              <div className="date-col">
                <div className="form">
                  <label className="form-label">
                    Start Time <span className="is-required">*</span>
                  </label>
                  <TimePicker
                    format="HH:mm"
                    placeholder="00:00"
                    value={timeValue(form.startTime)}
                    onChange={(t) =>
                      update('startTime', t ? t.format('HH:mm') : null)
                    }
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              <div className="date-col">
                <div className="form">
                  <label className="form-label">
                    End Time <span className="is-required">*</span>
                  </label>
                  <TimePicker
                    format="HH:mm"
                    placeholder="00:00"
                    value={timeValue(form.endTime)}
                    onChange={(t) =>
                      update('endTime', t ? t.format('HH:mm') : null)
                    }
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            </div>
          )}

          <h3 className="section-title mt-24">Content</h3>
          <div className="form">
            <label className="form-label">Select Media</label>
            {form.mediaIds.length === 0 ? (
              <button
                type="button"
                className="select-media-trigger"
                onClick={() => setMediaModalOpen(true)}
              >
                <i className="feather-search" />
                <span>Choose from inventory</span>
                <i className="feather-chevron-right" />
              </button>
            ) : (
              <div className="selected-media-list">
                {form.mediaIds.map((mid) => {
                  const media =
                    selectedMediaCache[mid] ||
                    (mediaList || []).find((m) => m._id === mid);
                  if (!media) return null;
                  return (
                    <div
                      key={mid}
                      className="selected-media-row"
                      onClick={() => setMediaModalOpen(true)}
                    >
                      <Cards
                        data={media}
                        type="media"
                        isShowMedia
                        isEdit
                        editAction={() => setMediaModalOpen(true)}
                        isDelete
                        deleteAction={() =>
                          update(
                            'mediaIds',
                            form.mediaIds.filter((x) => x !== mid)
                          )
                        }
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </form>

      <div className="schedule-form-bottom">
        <h3 className="section-title">Content Selection &amp; Playlist Order</h3>
        <div className="form">
          <label className="form-label">
            Assignee <span className="is-required">*</span>
          </label>
          <Select
            mode="multiple"
            placeholder="Select Screen"
            value={form.screenIds}
            onChange={(v) => update('screenIds', v)}
            style={{ width: '100%' }}
            optionFilterProp="children"
          >
            {(screens || []).map((s) => (
              <Select.Option key={s._id || s.id} value={s._id || s.id}>
                {s.name}
              </Select.Option>
            ))}
          </Select>
        </div>

        {conflict && (
          <ConflictBanner conflict={conflict} onClose={() => setConflict(null)} />
        )}

        {form.screenIds.length > 0 && form.mediaIds.length > 0 && (
          <TimelineSequence
            screens={screens}
            screenIds={form.screenIds}
            timeline={timeline}
            blockDuration={totalDuration}
            blocks={blocks}
            setBlocks={setBlocks}
            editId={id}
            mediaCache={selectedMediaCache}
            selectedMediaIds={form.mediaIds}
            conflict={conflict}
          />
        )}
      </div>
    </div>
  );
};

export default ScheduleForm;
